import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";

const WHEN = {
  packing_finish: "after Finish packing (Ready to Ship) → CHINA warehouse",
  mark_received: "when Marked received → USA (🇺🇸 flag) or CANADA warehouse",
  now: "right now",
};

/** ⏸ On Hold / Remove hold — Back Office & Super Admin only (rule Jun 2026). One click + a short reason. */
export function ShipHoldButton({ order, className = "" }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const held = !!order.ship_hold;
  const stop = order.ship_hold_stop;
  const hidden = ["fulfilled", "cancelled"].includes(order.status) || order.cancelled || order.cancel_pending;
  const act = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/ship-hold${held ? "/release" : ""}`, { reason: reason.trim() }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const s = r?.data?.ship_hold_stop;
      toast.success(held
        ? `▶ ${t("Hold removed")} — ${order.po_number} ${t("continues its regular path")}`
        : `⏸ ${order.po_number} ${t("ON HOLD")} — ${s?.due ? `🛑 ${t("STOP due now")} · ${t(s.warehouse_label || "")}` : `🛑 ${t("STOP")} ${t(s?.when || "")}`}`, { duration: 7000 });
      setOpen(false);
      setReason("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update the hold"),
  });
  if (hidden) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setReason(""); }}>
      <Button
        onClick={() => setOpen(true)}
        className={`whitespace-nowrap ${held ? "bg-red-700 hover:bg-red-800 text-white animate-pulse" : "bg-white hover:bg-red-50 text-red-700 border border-red-300"} ${className}`}
        data-testid="ship-hold-btn"
        title={held ? `${t("ON HOLD")} — ${stop?.reason || ""} · ${stop?.by || ""}` : t("Stop this order and store it in the warehouse until the hold is removed")}
      >
        {held ? `⏸ ${t("ON HOLD")} · ${t("Remove")}` : `⏸ ${t("On Hold")}`}
      </Button>
      <DialogContent className="max-w-md" data-testid="ship-hold-dialog">
        <DialogHeader>
          <DialogTitle>{held ? `▶ ${t("Remove the hold on")} ${order.po_number}` : `⏸ ${t("Put")} ${order.po_number} ${t("on hold")}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1 text-sm">
          {held ? (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900" data-testid="ship-hold-current">
              <div className="font-semibold">⏸ {t("On hold since")} {stop?.at ? new Date(stop.at).toLocaleString() : "—"} · {stop?.by || "—"}</div>
              <div className="mt-0.5">{t("Reason")}: {stop?.reason || "—"}</div>
              <div className="mt-0.5 text-xs">
                {stop?.stored
                  ? <>📦 {t("Stored in")} {t(stop.warehouse_label || "")} {t("by")} {stop.stored.by} — {t("the station must pull the box from the shelf and continue")}</>
                  : stop?.due ? <>🛑 {t("STOP due now")} · {t(stop.warehouse_label || "")}</> : <>🛑 {t("STOP")} {t(stop?.when || "")}</>}
              </div>
              <div className="mt-1 text-xs">{t("After removal the order continues its regular path to delivery.")}</div>
            </div>
          ) : (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 text-xs" data-testid="ship-hold-explain">
              🛑 {t("A big red STOP will tell the station to store the set — not ship it —")} <b>{t(WHEN[order.status === "shipping" ? (order.shipping_tracking_number ? "mark_received" : "now") : ["completed", "inventory_packing"].includes(order.status) ? "now" : order.status === "pickup_3pl" ? "mark_received" : "packing_finish"])}</b>.
              {" "}{t("If the order is cancelled while on hold, the set is added to that warehouse's stock automatically.")}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">{t("Reason")} <span className="text-red-600">*</span></div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={held ? t("Why is the hold removed?") : t("Why is this order on hold?")} data-testid="ship-hold-reason" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="ship-hold-cancel">{t("Cancel")}</Button>
            <Button className={held ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"} disabled={reason.trim().length < 2 || act.isPending} onClick={() => act.mutate()} data-testid="ship-hold-confirm">
              {act.isPending ? t("Saving…") : held ? `▶ ${t("Remove hold")}` : `⏸ ${t("Put on hold")}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
